/* eslint-disable no-useless-escape */
import {
  DigitsOnlyMessage,
  RequiredMessage,
  NotBlankMessage,
  MaxCharMessage,
  EmailMessage,
  AlphanumericMessage,
  TickerMessage,
} from '../../constants/validationMessage';
import * as Yup from 'yup';

export const regExp = {
  text: /^[a-zA-Z]+$/,
  decimal: /^(\d+|\d{1,3}(,\d{3})*)(\.\d{0,4})?$/,
  // decimal: /^\d*(\.\d{0,2})?$/,
  decimalFour: /^\d*(\.\d{0,4})?$/,
  decimalTwo: /^(\d+|\d{1,3}(,\d{3})*)(\.\d{0,2})?$/,
  textWithSpace: /^[A-Za-z\s]+$/,
  textWithoutSpace: /^\s*\S[\s\S]*$/,
  textWithNumber: /^[a-zA-Z0-9]+$/,
  textWithNumberSmallBrackets: /^[a-zA-Z0-9()]+$/,
  textWithNumberAndDot: /^[a-zA-Z0-9 .,]*$/,
  addressValidation: /^[a-zA-Z0-9 .,'-]*$/,
  textWithNumberAndSpace: /^[a-zA-Z0-9 ]*$/,
  textWithHyphenAndApostrophe: /^[a-zA-Z-'']+$/,
  email: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
  contact: /^(98|97)([0-9]{8})$/,
  digits: /^[0-9][0-9]*$/,
  digitsWithComma: /^[0-9,]*$/,
  negativeDigits: /^[0-9]*$/,
  floatDigits: /^[0-9.,]*$/,
  numericPhone: /^[0-9-][0-9-]*$/,
  number: /^[0-9][0-9]*$/,
  numberWithSpecialCharacter: /^[0-9#$%^&*()@!-/.,]*$/,
  landline: /^01[0-9]+$/,
  textWithNumberWithoutZeroLeading: /^[a-zA-Z1-9_ ][a-zA-Z0-9_ ]*$/,
  phone: /^((\\([0-9]{3}\\)[ \\-]*)|([0-9]{3})[ \\-]*)*?[0-9]{4}?$/,
  url: /^(?:http(s)?:\/\/)?(?:localhost(?::[0-9]{1,5})?|(?:[\w.-]+(?:\.[\w\.-]+)+))[\w\-\._~:/?#[\]@!\$&'\(\)\*\+,;=.]*$/gm,
  urlWithProtocol:
    /^https?:\/\/(?:localhost(?::[0-9]{1,5})?|(?:[\w.-]+(?:\.[\w\.-]+)+))[\w\-\._~:/?#[\]@!\$&'\(\)\*\+,;=.]*$/gm,
  ticker: /^[A-Z]{1,10}([-\.][A-Z]{1,3})?$/,
};

interface commonValidationType {
  required: boolean;
  decimal?: boolean;
  decimalFour?: boolean;
  decimalTwo?: boolean;
  floatDigitsTwo?: boolean;
  email?: boolean;
  noBlank?: boolean;
  alphanumeric?: boolean;
  alphanumericDot?: boolean;
  alphanumericSpace?: boolean;
  digits?: boolean;
  digitsWithComma?: boolean;
  negativeDigits?: boolean;
  floatDigits?: boolean;
  numericPhone?: boolean;
  textLimit?: number;
  min?: number;
  minValue?: number;
  max?: number;
  isNumber?: boolean;
  isBoolean?: boolean;
  isDate?: boolean;
  doubleSpace?: boolean;
  textWithHyphen?: boolean;
  floatValue?: number;
  exercisePrice?: boolean;
  alphanumericWithHyphen?: boolean;
  ticker?: boolean;
  alphaNumWithBasicSymbols?: boolean;
  label?: string;
  allowSmallBrackets?: boolean;
  url?: boolean;
  urlWithProtocol?: boolean;
}

export const commonValidation = ({
  isNumber = false,
  isBoolean = false,
  isDate = false,
  required,
  decimal,
  decimalFour,
  decimalTwo,
  floatDigitsTwo,
  email,
  noBlank,
  minValue,
  alphanumeric,
  alphanumericDot,
  alphanumericSpace,
  numericPhone,
  negativeDigits,
  digits,
  digitsWithComma,
  floatDigits,
  textLimit,
  min,
  max,
  doubleSpace = false,
  textWithHyphen = false,
  exercisePrice,
  alphanumericWithHyphen = false,
  floatValue,
  ticker = false,
  alphaNumWithBasicSymbols = false,
  label,
  allowSmallBrackets = false,
  url = false,
  urlWithProtocol = false,
}: commonValidationType): any => {
  let textYup: any = Yup.string();

  if (isNumber) {
    textYup = Yup.number();
  }
  if (isDate) {
    textYup = Yup.date().typeError('Required field');
  }

  if (isBoolean) {
    textYup = Yup.boolean();
  }

  if (textLimit != null) {
    textYup = textYup.max(textLimit, MaxCharMessage(textLimit));
  }

  if (required) {
    textYup = textYup.required(RequiredMessage);
  }

  if (min != null) {
    textYup =
      minValue != null
        ? textYup.min(
            min,
            minValue !== 0 ? `Must be ${minValue} digits` : `Must be greater than ${min - 1}`
          )
        : textYup.min(min, `Must be greater than ${min - 1}`);
  }

  if (max != null) {
    textYup = textYup.max(max, `Must be ${max} digits`);
  }

  if (email ?? false) {
    textYup = textYup.email(EmailMessage);
  }

  if (alphanumeric ?? false) {
    textYup = textYup.matches(regExp.textWithNumber, AlphanumericMessage);
  }
  if (alphanumericDot ?? false) {
    textYup = textYup.matches(regExp.textWithNumberAndDot, AlphanumericMessage);
  }
  if (alphanumericSpace ?? false) {
    textYup = textYup.matches(regExp.textWithNumberAndSpace, AlphanumericMessage);
  }
  if (alphanumericWithHyphen ?? false) {
    textYup = textYup.matches(regExp.addressValidation, AlphanumericMessage);
  }
  if (digits ?? false) {
    textYup = textYup.matches(regExp.digits, DigitsOnlyMessage);
  }
  if (negativeDigits ?? false) {
    textYup = textYup.matches(regExp.negativeDigits, 'Must be whole number');
  }
  if (decimal) {
    textYup = textYup.matches(regExp.decimal, 'Must be 4 decimal place only');
  }

  if (decimalTwo) {
    textYup = textYup.matches(regExp.decimalTwo, 'Must be 2 decimal places only');
  }

  if (digitsWithComma ?? false) {
    textYup = textYup.matches(regExp.digitsWithComma, DigitsOnlyMessage);
  }

  if (floatDigits ?? false) {
    textYup = Yup.string()
      .test('float', function (value: any) {
        const splitValue = Boolean(value) && value.split('.');
        if (Boolean(value?.includes('.')) && splitValue[1].length > 4) {
          return this.createError({
            path: this.path,
            message: `Must be 4 decimal place only`,
          });
        } else {
          return true;
        }
      })
      .concat(textYup);
    // textYup.matches(regExp.floatDigits, DigitsOnlyMessage);
  }

  if (floatDigitsTwo ?? false) {
    textYup = Yup.string()
      .test('floatTwo', function (value: any) {
        const splitValue = Boolean(value) && value.split('.');
        if (Boolean(value?.includes('.')) && splitValue[1].length > 2) {
          return this.createError({
            path: this.path,
            message: `Must be 2 decimal places only`,
          });
        } else {
          return true;
        }
      })
      .concat(textYup);
  }

  if (exercisePrice ?? false) {
    textYup = Yup.string()
      .test('float', function (value: any) {
        const splitValue = Boolean(value) && value.split('.');
        if (Boolean(value?.includes('.')) && splitValue[1].length > 6) {
          return this.createError({
            path: this.path,
            message: `Must be  6 decimal place only`,
          });
        } else {
          return true;
        }
      })
      .concat(textYup);
    // textYup.matches(regExp.floatDigits, DigitsOnlyMessage);
  }

  if (numericPhone ?? false) {
    textYup = textYup.matches(regExp.numericPhone, DigitsOnlyMessage);
  }
  if (noBlank ?? false) {
    textYup = textYup.matches(regExp.textWithoutSpace, NotBlankMessage);
  }

  if (ticker) {
    textYup = textYup.matches(regExp.ticker, TickerMessage);
  }

  if (doubleSpace) {
    textYup = Yup.string()
      .test('double-space', function (value) {
        const validation: boolean = (value?.includes('  ') ?? false) || false;
        if (validation) {
          return this.createError({
            path: this.path,
            message: 'Must not contain double space ',
          });
        } else {
          return true;
        }
      })
      .concat(textYup);
  }

  if (textWithHyphen ?? false) {
    textYup = textYup.matches(regExp.textWithHyphenAndApostrophe, 'Invalid ${label}');
  }

  if (alphaNumWithBasicSymbols) {
    textYup = textYup.matches(/^[a-z,0-9, @()-/*"':;_#-+]+$/i, `Invalid ${label}`);
  }

  if (allowSmallBrackets) {
    textYup = textYup.matches(regExp.textWithNumberSmallBrackets, AlphanumericMessage);
  }

  if (url) {
    textYup = textYup.matches(regExp.url, 'Invalid URL');
  }

  if (urlWithProtocol) {
    textYup = textYup.matches(regExp.urlWithProtocol, 'Invalid URL');
  }

  return textYup.nullable();
};
